import { Box, Button, FormHelperText, Popover, TextField } from "@mui/material";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../context/toast-context";
import { ScheduleRequestDto } from "../../services/dto/response/schedule-response-dto";
import { ScheduleService } from "../../services/schedule-service";
import { Branch, Schedule } from "../../types";
import { ButtonForm } from "./button-form";

interface AddScheduleFormProps {
  selectedBranch: Branch;
  setIsModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  onAddSchedule?: (branchId: number, schedules: ScheduleRequestDto[]) => void;
  scheduleToEdit?: Schedule | null;
  onScheduleUpdated?: () => void;
  onSuccess?: () => void;
}

const TimePicker = ({ value, onChange, label, error, helperText, isEndTime, startTimeValue, endTimeValue }: { 
  value: string, 
  onChange: (value: string) => void, 
  label: string,
  error?: boolean,
  helperText?: string,
  isEndTime?: boolean,
  startTimeValue?: string,
  endTimeValue?: string
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [tempValue, setTempValue] = useState(value);
  const inputRef = useRef<HTMLDivElement>(null);
  const hoursRef = useRef<HTMLDivElement>(null);
  const minutesRef = useRef<HTMLDivElement>(null);

  const isTimeDisabled = (hours: number) => {
    if (isEndTime && startTimeValue) {
      const [startHours, startMinutes] = startTimeValue.split(':').map(Number);
      if (hours === startHours) {
        // Solo habilitar la hora si hay al menos un minuto mayor al de inicio
        return !Array.from({ length: 12 }, (_, i) => i * 5)
          .some(m => m > startMinutes);
      }
      return (hours * 60) <= (startHours * 60 + startMinutes);
    } else if (!isEndTime && endTimeValue) {
      const [endHours, endMinutes] = endTimeValue.split(':').map(Number);
      if (hours === endHours) {
        // Solo habilitar la hora si hay al menos un minuto menor al de fin
        return !Array.from({ length: 12 }, (_, i) => i * 5)
          .some(m => m < endMinutes);
      }
      return (hours * 60) >= (endHours * 60 + endMinutes);
    }
    return false;
  };

  const isMinutesDisabled = (minutes: number) => {
    const currentHour = parseInt(tempValue.split(':')[0] || '0');

    if (isEndTime && startTimeValue) {
      const [startHours, startMinutes] = startTimeValue.split(':').map(Number);
      
      // Si es la misma hora que la hora de inicio, solo deshabilitar los minutos menores o iguales
      if (currentHour === startHours) {
        return minutes <= startMinutes;
      }
      
      return false;
    } else if (!isEndTime && endTimeValue) {
      const [endHours, endMinutes] = endTimeValue.split(':').map(Number);
      
      // Si es la misma hora que la hora de fin, solo deshabilitar los minutos mayores o iguales
      if (currentHour === endHours) {
        return minutes >= endMinutes;
      }
      
      return false;
    }
    
    return false;
  };

  const handleClick = () => {
    setTempValue(value);
    setAnchorEl(inputRef.current);
    
    setTimeout(() => {
      if (value) {
        const [hours, minutes] = value.split(':').map(Number);
        
        if (hoursRef.current) {
          const hourElement = hoursRef.current.children[hours] as HTMLElement;
          if (hourElement) {
            hourElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        }
        
        if (minutesRef.current) {
          const minuteIndex = Math.floor(minutes / 5);
          const minuteElement = minutesRef.current.children[minuteIndex] as HTMLElement;
          if (minuteElement) {
            minuteElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        }
      }
    }, 100);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAccept = () => {
    onChange(tempValue);
    handleClose();
  };

  const handleTimeSelect = (hours: number, minutes?: number) => {
    if (minutes !== undefined && !tempValue.split(':')[0]) {
      return;
    }

    const currentMinutes = tempValue.split(':')[1] || '00';
    const formattedHours = hours.toString().padStart(2, '0');
    const formattedMinutes = minutes !== undefined ? minutes.toString().padStart(2, '0') : currentMinutes;
    const newValue = `${formattedHours}:${formattedMinutes}`;
    const newTotalMinutes = hours * 60 + (minutes !== undefined ? minutes : parseInt(currentMinutes));

    // Validar antes de actualizar el valor
    if (isEndTime && startTimeValue) {
      const [startHours, startMinutes] = startTimeValue.split(':').map(Number);
      const startTotalMinutes = startHours * 60 + startMinutes;
      
      // Si es la misma hora y se está seleccionando solo la hora (sin minutos), permitir
      if (hours === startHours && minutes === undefined) {
        setTempValue(newValue);
        return;
      }
      
      // Si es la misma hora, permitir seleccionar minutos posteriores
      if (hours === startHours && minutes !== undefined) {
        if (minutes <= startMinutes) {
          return;
        }
      } else if (newTotalMinutes <= startTotalMinutes) {
        return;
      }
    } else if (!isEndTime && endTimeValue) {
      const [endHours, endMinutes] = endTimeValue.split(':').map(Number);
      const endTotalMinutes = endHours * 60 + endMinutes;
      
      // Si es la misma hora y se está seleccionando solo la hora (sin minutos), permitir
      if (hours === endHours && minutes === undefined) {
        setTempValue(newValue);
        return;
      }
      
      // Si es la misma hora, permitir seleccionar minutos anteriores
      if (hours === endHours && minutes !== undefined) {
        if (minutes >= endMinutes) {
          return;
        }
      } else if (newTotalMinutes >= endTotalMinutes) {
        return;
      }
    }

    setTempValue(newValue);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <TextField
        ref={inputRef}
        value={value}
        label={label}
        error={error}
        helperText={helperText}
        variant="standard"
        onClick={handleClick}
        InputLabelProps={{
          shrink: true,
        }}
        inputProps={{
          readOnly: true,
          style: {
            fontSize: '1rem',
            fontFamily: 'Montserrat',
            padding: '8px 0',
            cursor: 'pointer'
          }
        }}
        sx={{
          width: "200px",
          "& .MuiInput-root": {
            color: "#000",
            fontFamily: "Montserrat",
            "&:before": {
              borderBottom: "1px solid #838383",
            },
            "&:hover:not(.Mui-disabled):before": {
              borderBottomColor: "#898989",
            },
          },
          "& label.Mui-focused": {
            color: "#7e7e7e",
          },
          "& .MuiInputLabel-standard": {
            color: "#000",
            fontWeight: "400",
            fontFamily: "Montserrat",
          },
          "& .MuiInput-underline:after": {
            borderBottomColor: "#838383",
          },
        }}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            width: '320px',
            maxWidth: 'none',
            padding: '0',
            borderRadius: '8px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
            overflow: 'hidden',
            animation: 'fadeIn 0.2s ease-in-out',
            '@keyframes fadeIn': {
              '0%': {
                opacity: 0,
                transform: 'translateY(-8px)'
              },
              '100%': {
                opacity: 1,
                transform: 'translateY(0)'
              }
            }
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px',
            borderBottom: '1px solid #e0e0e0',
            backgroundColor: '#fff'
          }}>
            <Box sx={{ 
              fontSize: '1rem',
              fontFamily: 'Montserrat',
              fontWeight: 500,
              color: '#202124',
              letterSpacing: '0.25px',
              userSelect: 'none'
            }}>
              Seleccionar hora
            </Box>
            <Button
              onClick={handleClose}
              sx={{
                minWidth: 'auto',
                padding: '4px',
                color: '#5f6368',
                borderRadius: '50%',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: '#f1f3f4',
                  transform: 'scale(1.1)'
                }
              }}
            >
              ✕
            </Button>
          </Box>

          {/* Contenido principal */}
          <Box sx={{ 
            display: 'flex',
            padding: '16px',
            gap: '16px',
            backgroundColor: '#fff'
          }}>
            {/* Selector de horas */}
            <Box sx={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              <Box sx={{ 
                fontSize: '0.75rem',
                color: '#5f6368',
                fontFamily: 'Montserrat',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                userSelect: 'none'
              }}>
                Hora
              </Box>
              <Box 
                ref={hoursRef}
                sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  paddingRight: '8px',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                    marginLeft: '8px'
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f3f4',
                    borderRadius: '4px',
                    margin: '4px 0'
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#dadce0',
                    borderRadius: '4px',
                    '&:hover': {
                      background: '#bdc1c6'
                    }
                  }
                }}
              >
                {Array.from({ length: 24 }, (_, i) => {
                  const isDisabled = isTimeDisabled(i);
                  return (
                    <Box
                      key={i}
                      onClick={() => !isDisabled && handleTimeSelect(i, undefined)}
                      sx={{
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 12px',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        color: isDisabled ? '#bdc1c6' : (tempValue.startsWith(i.toString().padStart(2, '0')) ? '#1a73e8' : '#3c4043'),
                        backgroundColor: isDisabled ? '#f8f9fa' : (tempValue.startsWith(i.toString().padStart(2, '0')) ? '#e8f0fe' : 'transparent'),
                        fontWeight: tempValue.startsWith(i.toString().padStart(2, '0')) ? 500 : 400,
                        userSelect: 'none',
                        width: '100%',
                        '&:hover': isDisabled ? {} : {
                          backgroundColor: '#f1f3f4',
                          transform: 'translateX(2px)'
                        },
                        '&:active': isDisabled ? {} : {
                          transform: 'scale(0.98)'
                        }
                      }}
                    >
                      {i.toString().padStart(2, '0')}
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Selector de minutos */}
            <Box sx={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              <Box sx={{ 
                fontSize: '0.75rem',
                color: '#5f6368',
                fontFamily: 'Montserrat',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                userSelect: 'none'
              }}>
                Minutos
              </Box>
              <Box 
                ref={minutesRef}
                sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  paddingRight: '8px',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                    marginLeft: '8px'
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f3f4',
                    borderRadius: '4px',
                    margin: '4px 0'
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#dadce0',
                    borderRadius: '4px',
                    '&:hover': {
                      background: '#bdc1c6'
                    }
                  }
                }}
              >
                {Array.from({ length: 13 }, (_, i) => {
                  const minutes = i === 12 ? 59 : i * 5;
                  const isDisabled = !tempValue.split(':')[0] || isMinutesDisabled(minutes);
                  return (
                    <Box
                      key={minutes}
                      onClick={() => !isDisabled && handleTimeSelect(parseInt(tempValue.split(':')[0] || '0'), minutes)}
                      sx={{
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 12px',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        color: isDisabled ? '#bdc1c6' : (tempValue.endsWith(minutes.toString().padStart(2, '0')) ? '#1a73e8' : '#3c4043'),
                        backgroundColor: isDisabled ? '#f8f9fa' : (tempValue.endsWith(minutes.toString().padStart(2, '0')) ? '#e8f0fe' : 'transparent'),
                        fontWeight: tempValue.endsWith(minutes.toString().padStart(2, '0')) ? 500 : 400,
                        userSelect: 'none',
                        width: '100%',
                        '&:hover': isDisabled ? {} : {
                          backgroundColor: '#f1f3f4',
                          transform: 'translateX(2px)'
                        },
                        '&:active': isDisabled ? {} : {
                          transform: 'scale(0.98)'
                        }
                      }}
                    >
                      {minutes.toString().padStart(2, '0')}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          {/* Footer */}
          <Box sx={{ 
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
            padding: '8px 16px',
            borderTop: '1px solid #e0e0e0',
            backgroundColor: '#fff'
          }}>
            <Button
              onClick={handleClose}
              sx={{
                textTransform: "none",
                fontFamily: "Montserrat",
                fontWeight: 500,
                fontSize: '0.875rem',
                padding: '6px 16px',
                color: '#5f6368',
                borderRadius: '4px',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: '#f1f3f4',
                  transform: 'translateY(-1px)'
                },
                '&:active': {
                  transform: 'translateY(0)'
                }
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAccept}
              sx={{
                textTransform: "none",
                fontFamily: "Montserrat",
                fontWeight: 500,
                fontSize: '0.875rem',
                padding: '6px 16px',
                color: '#1a73e8',
                borderRadius: '4px',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: '#e8f0fe',
                  transform: 'translateY(-1px)'
                },
                '&:active': {
                  transform: 'translateY(0)'
                }
              }}
            >
              Aceptar
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export function AddScheduleForm({
  selectedBranch,
  setIsModalOpen,
  onAddSchedule,
  scheduleToEdit,
  onScheduleUpdated,
  onSuccess,
}: AddScheduleFormProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const scheduleService = useMemo(() => new ScheduleService(), []);
  const { showToast } = useToast();

  const [errors, setErrors] = useState({
    name: "",
    schedulesDays: {} as { [key: number]: string },
  });

  const [formData, setFormData] = useState<ScheduleRequestDto>(() => {
    if (scheduleToEdit) {
      return {
        name: scheduleToEdit.name,
        schedulesDays: scheduleToEdit.schedulesDays.map(day => ({
          dayOfWeek: day.dayOfWeek,
          schedulesBlocks: day.schedulesBlocks.map(block => ({
            startTime: block.startTime.split(":").slice(0, 2).join(":"),
            endTime: block.endTime.split(":").slice(0, 2).join(":"),
          })),
        })),
        branchId: selectedBranch.branchId,
      };
    }
    return {
      name: "",
      schedulesDays: [],
      branchId: selectedBranch.branchId,
    };
  });

  const validate = () => {
    let isValid = true;
    const newErrors = {
      name: "",
      schedulesDays: {} as { [key: number]: string },
    };

    if (!formData.name) {
      newErrors.name = t("branches.schedules.create.errors.name");
      isValid = false;
    }

    if (formData.schedulesDays.length === 0) {
      newErrors.schedulesDays[0] = t("branches.schedules.create.errors.dayOfWeek");
      isValid = false;
    }

    formData.schedulesDays.forEach((day, index) => {
      day.schedulesBlocks.forEach((block, blockIndex) => {
        if (!block.startTime || !block.endTime) {
          newErrors.schedulesDays[day.dayOfWeek] = t("branches.schedules.create.errors.emptyBlock");
          isValid = false;
        } else {
          const [startHour, startMinute] = block.startTime.split(":").map(Number);
          const [endHour, endMinute] = block.endTime.split(":").map(Number);
          const start = startHour * 60 + startMinute;
          const end = endHour * 60 + endMinute;
          
          if (start >= end) {
            newErrors.schedulesDays[day.dayOfWeek] = t("branches.schedules.create.errors.invalidRange");
            isValid = false;
          }
        }
      });
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    if (!validate()) {
      setLoading(false);
      const firstErrorElement = document.querySelector('.error-message');
      if (firstErrorElement) {
        const container = firstErrorElement.closest('.schedule-container') || firstErrorElement.closest('.name-container');
        if (container) {
          container.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
      return;
    }

    try {
      if (scheduleToEdit) {
        await scheduleService.updateSchedule(
          {
            scheduleId: scheduleToEdit.scheduleId,
            schedule: formData,
          },
          () => {
            showToast(t("branches.schedules.edit.success"), "success");
            onScheduleUpdated?.();
          },
          (errorMessage: string) => {
            showToast(errorMessage, "error");
          }
        );
      } else {
        await scheduleService.addSchedule(
          formData,
          async () => {
            showToast(t("branches.schedules.create.success"), "success");
            onAddSchedule?.(selectedBranch.branchId, [formData]);
            onSuccess?.();
            return Promise.resolve();
          },
          (errorMessage: string) => {
            showToast(errorMessage, "error");
          }
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDayToggle = (day: number) => {
    setFormData(prev => {
      const dayIndex = prev.schedulesDays.findIndex(d => d.dayOfWeek === day);
      if (dayIndex === -1) {
        // Agregar nuevo día
        return {
          ...prev,
          schedulesDays: [
            ...prev.schedulesDays,
            {
              dayOfWeek: day,
              schedulesBlocks: [{
                startTime: "",
                endTime: "",
              }]
            }
          ]
        };
      } else {
        // Remover día existente
        return {
          ...prev,
          schedulesDays: prev.schedulesDays.filter((_, index) => index !== dayIndex)
        };
      }
    });
  };

  const handleTimeChange = (dayIndex: number, blockIndex: number, field: 'startTime' | 'endTime', value: string) => {
    setFormData(prev => ({
      ...prev,
      schedulesDays: prev.schedulesDays.map((day, dIndex) => {
        if (dIndex === dayIndex) {
          return {
            ...day,
            schedulesBlocks: day.schedulesBlocks.map((block, bIndex) => {
              if (bIndex === blockIndex) {
                return {
                  ...block,
                  [field]: value
                };
              }
              return block;
            })
          };
        }
        return day;
      })
    }));
  };

  const addTimeBlock = (dayIndex: number) => {
    setFormData(prev => ({
      ...prev,
      schedulesDays: prev.schedulesDays.map((day, index) => {
        if (index === dayIndex) {
          return {
            ...day,
            schedulesBlocks: [
              ...day.schedulesBlocks,
              {
                startTime: "",
                endTime: "",
              }
            ]
          };
        }
        return day;
      })
    }));
  };

  const removeTimeBlock = (dayIndex: number, blockIndex: number) => {
    setFormData(prev => ({
      ...prev,
      schedulesDays: prev.schedulesDays.map((day, index) => {
        if (index === dayIndex) {
          return {
            ...day,
            schedulesBlocks: day.schedulesBlocks.filter((_, bIndex) => bIndex !== blockIndex)
          };
        }
        return day;
      })
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="p-6">
      <div className="grid gap-4">
        <div className="name-container">
          <TextField
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            label={t("branches.schedules.create.name")}
            error={!!errors.name}
            helperText={errors.name}
            variant="standard"
            className="error-message"
            sx={{
              width: "100%",
              "& .MuiInput-root": {
                color: "#000",
                fontFamily: "Montserrat",
                "&:before": {
                  borderBottom: "1px solid #838383",
                },
                "&:hover:not(.Mui-disabled):before": {
                  borderBottomColor: "#898989",
                },
              },
              "& label.Mui-focused": {
                color: "#7e7e7e",
              },
              "& .MuiInputLabel-standard": {
                color: "#000",
                fontWeight: "400",
                fontFamily: "Montserrat",
              },
              "& .MuiInput-underline:after": {
                borderBottomColor: "#838383",
              },
            }}
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {[0, 1, 2, 3, 4, 5, 6].map((day) => (
            <Button
              key={day}
              variant={formData.schedulesDays.some(d => d.dayOfWeek === day) ? "contained" : "outlined"}
              onClick={() => handleDayToggle(day)}
              sx={{
                minWidth: "100px",
                textTransform: "none",
                fontFamily: "Montserrat",
                fontWeight: 300,
              }}
            >
              {t(`days.${day}`)}
            </Button>
          ))}
        </div>

        {formData.schedulesDays.map((day, dayIndex) => (
          <div key={day.dayOfWeek} className="border rounded-lg p-4 schedule-container">
            <div className="flex justify-between items-center mb-4">
              <div className="font-semibold">{t(`days.${day.dayOfWeek}`)}</div>
              <Button
                variant="contained"
                onClick={() => addTimeBlock(dayIndex)}
                sx={{
                  background: "#2d2f32",
                  margin: 0,
                  padding: "0 10px",
                  borderRadius: "999px",
                  textTransform: "none",
                  fontFamily: "Montserrat",
                  fontWeight: 300,
                }}
              >
                {t("branches.schedules.create.add-time")}
              </Button>
            </div>
            {day.schedulesBlocks.map((block, blockIndex) => (
              <div key={blockIndex} className="flex gap-4 items-center w-full mb-4">
                <TimePicker
                  value={block.startTime}
                  onChange={(value) => handleTimeChange(dayIndex, blockIndex, 'startTime', value)}
                  label={t("branches.schedules.create.startTime")}
                  error={false}
                  endTimeValue={block.endTime}
                />

                <TimePicker
                  value={block.endTime}
                  onChange={(value) => handleTimeChange(dayIndex, blockIndex, 'endTime', value)}
                  label={t("branches.schedules.create.endTime")}
                  error={false}
                  isEndTime={true}
                  startTimeValue={block.startTime}
                />

                {blockIndex > 0 && (
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => removeTimeBlock(dayIndex, blockIndex)}
                    sx={{
                      minWidth: "auto",
                      padding: "4px 8px",
                    }}
                  >
                    {t("branches.schedules.create.remove")}
                  </Button>
                )}
              </div>
            ))}
            {errors.schedulesDays[day.dayOfWeek] && (
              <FormHelperText error className="error-message">
                {errors.schedulesDays[day.dayOfWeek]}
              </FormHelperText>
            )}
          </div>
        ))}

        <div className="w-full flex flex-col gap-4">
          <ButtonForm loading={loading}>
            {scheduleToEdit ? t("branches.schedules.edit.save") : t("branches.schedules.create.submit")}
          </ButtonForm>
        </div>
      </div>
    </form>
  );
} 